// ─── Alerta — la BANDA DE RESULTADO del diseño (I-244; antes BUG-2388, T14·9) ─
// Fuente: `handoff_claude_ingresos_vestido/README.md:96-99` y `casa-ingresos-vestido.jsx:184-207` (`BandaResultado`).
// 4 tonos — bien / mal / ojo / cargando — (+ `neutro`, que el mismo jsx define en `ING_TOK`): el título va EN el tono
// (12.5/700), el cuerpo en `fg2` en línea aparte, NUNCA en el color del tono (en oscuro se vuelve ilegible).
// Padding 11/13, radio 11, gap 10. No se va sola: la cierra el usuario (`onCerrar`) o la reemplaza la acción.
// Una sola forma: sin `tema` se viste con `useTema()` (el claro fuera de la casa).
// Alias vivos de la forma vieja (`colores`, `tituloColor`, `bordeFijo`, `icon`, `children`): 3 usuarios de la casa.
import React from 'react';
import { Check, AlertTriangle, Info, X } from 'lucide-react';
import { PASO, useTema, type CasaTema } from '@components/M02/casaTema';
import { Spinner } from './Spinner';
import { Button } from './Button';

export type TonoAlerta = 'ok' | 'error' | 'ojo' | 'cargando' | 'neutro';

/** Los colores de cada tono — `ING_TOK(T).tonos` del diseño. Los chips, las bandas y los vacíos leen ESTA misma tabla. */
export const TONOS_ALERTA = (T: CasaTema): Record<TonoAlerta, { fg: string; bg: string; bd: string }> => ({
  ok:       { fg: T.accent, bg: T.accentSoft, bd: T.accentLine },
  error:    { fg: T.danger, bg: T.dangerSoft, bd: T.dangerLine },
  // README:99 — «ojo» toma su borde de `lineSoft`, no de un token warn-línea que la casa no tiene.
  ojo:      { fg: T.warnInk,   bg: T.warnSoft,   bd: T.lineSoft },
  cargando: { fg: T.fg2,    bg: T.chip,       bd: T.line },
  neutro:   { fg: T.fg2,    bg: T.chip,       bd: T.lineSoft },
});

/** Alias de la forma vieja: el bloque de colores ya resuelto por quien llama (`casa/temaAlerta.ts`). */
export interface AlertaColores {
  bg: string;
  border: string;
  fg: string;
  accent: string;
}

/** `principal`: la acción que se pide primero (botón sólido; rojo en `error`) — las demás van secundarias. */
export interface AlertaAccion { label: string; onClick: () => void; principal?: boolean }

interface AlertaProps {
  tono?: TonoAlerta;
  titulo?: React.ReactNode;
  /** El cuerpo (12, `fg2`, en su propia línea). `children` es su alias. */
  texto?: React.ReactNode;
  children?: React.ReactNode;
  /** Botones chicos bajo el texto. */
  acciones?: AlertaAccion[];
  /** Pone la ✕ de cerrar (con `aria-label`). Sin esto la banda no se puede quitar: es lo que pide el diseño. */
  onCerrar?: () => void;
  tema?: CasaTema;
  role?: 'status' | 'alert';
  className?: string;
  /** `data-testid` de la banda (la pieza ya no se envuelve en un div sólo para ponérselo). */
  testId?: string;
  // ── alias de la forma vieja ──
  /** Sustituye los colores del tono (fondo, título y borde); el cuerpo toma `colores.fg`. */
  colores?: AlertaColores;
  /** Reemplaza el ícono del tono. */
  icon?: React.ReactNode;
  /** 'fg': el título se queda en el texto normal (lo pide `BandaCierre`, `casa-unidades-app.jsx:53-63`). */
  tituloColor?: 'accent' | 'fg';
  /** Borde fijo sin importar el tono. */
  bordeFijo?: string;
}

export const Alerta: React.FC<AlertaProps> = ({
  tono = 'ok', titulo, texto, children, acciones, onCerrar, tema, role = 'status', className, testId,
  colores, icon, tituloColor = 'accent', bordeFijo,
}) => {
  const delTema = useTema();
  const T = tema ?? delTema;
  const base = TONOS_ALERTA(T)[tono];
  const to = colores ? { fg: colores.accent, bg: colores.bg, bd: colores.border } : base;
  const cuerpo = texto ?? children;
  const Icono = tono === 'error' ? AlertTriangle : tono === 'ok' ? Check : Info;

  return (
    <div
      role={role}
      className={className}
      data-testid={testId}
      style={{
        padding: '11px 13px', borderRadius: 11, background: to.bg, border: `1px solid ${bordeFijo ?? to.bd}`,
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}
    >
      {icon
        ? <span style={{ flexShrink: 0, display: 'flex' }}>{icon}</span>
        : tono === 'cargando'
          ? <Spinner color={to.fg} size={13} />
          : <Icono size={14} color={to.fg} style={{ flexShrink: 0 }} aria-hidden />}
      <div style={{ minWidth: 0, flex: 1 }}>
        {titulo && (
          <div style={{ fontSize: PASO.s12_5, fontWeight: 700, color: tituloColor === 'fg' ? T.fg : to.fg }}>{titulo}</div>
        )}
        {cuerpo && (
          <div style={{ fontSize: PASO.s12, color: colores?.fg ?? T.fg2, marginTop: titulo ? 2 : 0, lineHeight: 1.5 }}>{cuerpo}</div>
        )}
        {acciones && acciones.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 9 }}>
            {acciones.map(a => (
              <Button key={a.label} tema={T} variant={a.principal ? (tono === 'error' ? 'danger' : 'primary') : 'secondary'} solido={a.principal} size="sm" onClick={a.onClick}>{a.label}</Button>
            ))}
          </div>
        )}
      </div>
      {onCerrar && (
        <button type="button" onClick={onCerrar} aria-label="Cerrar aviso"
          style={{ border: 0, background: 'transparent', cursor: 'pointer', display: 'flex', flexShrink: 0 }}>
          <X size={12} color={T.fg3} />
        </button>
      )}
    </div>
  );
};

export default Alerta;
